import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  Autocomplete,
  Checkbox,
  CircularProgress,
  Switch,
  FormControlLabel,
  Menu,
  MenuItem,
  Paper
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getSalesOrders, getSalesOrderDetail, generatePodUrl, downloadSalesOrderItemsCsv } from '../services/api';
import { useSoraFilterOptions } from '../Sora';
import { formatDate } from '../utils/tatCalculator';
import { saveAs } from 'file-saver';
import PODViewer from './PODViewer';

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const FILTER_DISPLAY_LIMIT = 100;

/**
 * Creates a stable PaperComponent for a given field.
 * Defined at module level so the component identity never changes between renders,
 * preventing MUI Autocomplete from unmounting/remounting the listbox on every keystroke
 * (mui/material-ui#31073). Dynamic data (selectedValues, handleFilterChange) is read
 * from a ref so the stable component always sees the latest values.
 */
const makeChipsPaper = (dataRef, field) => {
  const ChipsPaper = ({ children, ...paperProps }) => {
    const entry = dataRef.current[field] || {};
    const { selectedValues = [], handleFilterChange } = entry;
    return React.createElement(Paper, { ...paperProps, elevation: 3 },
      selectedValues.length > 0 && React.createElement(Box, {
        sx: {
          px: 1.5, pt: 1.25, pb: 0.75,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
          maxHeight: 96,
          overflowY: 'auto'
        }
      },
        selectedValues.map(val =>
          React.createElement(Chip, {
            key: val,
            label: val,
            size: 'small',
            onDelete: (e) => {
              e.stopPropagation();
              handleFilterChange(field, selectedValues.filter(v => v !== val));
            },
            onMouseDown: (e) => e.stopPropagation(),
            sx: {
              fontSize: '0.75rem',
              height: 22,
              backgroundColor: 'rgba(0,0,0,0.08)',
              '& .MuiChip-deleteIcon': { fontSize: '0.9rem' }
            }
          })
        )
      ),
      children
    );
  };
  ChipsPaper.displayName = 'ChipsPaper';
  return ChipsPaper;
};

const SalesOrderList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [salesOrders, setSalesOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [podViewerOpen, setPodViewerOpen] = useState(false);
  const [podGcsUri, setPodGcsUri] = useState(null);
  const [loadingPod, setLoadingPod] = useState(new Map());
  const [podExistsMap, setPodExistsMap] = useState(new Map());
  // Per-field search text typed in each dropdown — drives the 100-cap vs full-list logic
  const [filterInputValues, setFilterInputValues] = useState({});
  // Track which dropdown is currently open/focused — controls display text vs search text
  const [openDropdowns, setOpenDropdowns] = useState({});
  const allFilterOptionsRef = useRef({}); // Full distinct options loaded once on mount

  // Stable PaperComponent instances per field — created once, never re-created.
  // Each reads dynamic state (selectedValues, handleFilterChange) from a paired ref.
  const chipsPaperDataRef = useRef({});
  const chipsPaperComponents = useRef({});

  // Consume filter options prefetched by the parent Sora layout
  const { data: prefetchedFilterOptions, loading: filterOptionsLoading } = useSoraFilterOptions();
  useEffect(() => {
    if (prefetchedFilterOptions) {
      allFilterOptionsRef.current = prefetchedFilterOptions;
    }
  }, [prefetchedFilterOptions]);
  
  // Initialize filters from URL params (supporting arrays for multi-select)
  const parseFilterValue = (value) => {
    if (!value) return [];
    return value.includes(',') ? value.split(',') : [value];
  };

  // Build tat_breach array from URL (including breach flags from SalesOrderAlerts)
  const getTatBreachFromSearchParams = (params) => {
    const fromParam = parseFilterValue(params.get('tat_breach'));
    const arr = Array.isArray(fromParam) ? [...fromParam] : fromParam.length ? [fromParam] : [];
    if (params.get('dispatch_breach') === 'true') arr.push('Dispatch Breach');
    if (params.get('pickup_breach') === 'true') arr.push('Pickup Breach');
    if (params.get('delivery_breach') === 'true') arr.push('Delivery Breach');
    if (params.get('grc_breach') === 'true') arr.push('GRC Breach');
    return [...new Set(arr)];
  };

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    return dayjs().format('YYYY-MM-DD');
  };

  // Initialize filters from URL params (empty on first load, will be set in useEffect)
  const [filters, setFilters] = useState({
    start_date: searchParams.get('start_date') || '',
    end_date: searchParams.get('end_date') || '',
    date_field: searchParams.get('date_field') || 'SO_DATE', // Default to SO_DATE
    status: parseFilterValue(searchParams.get('status')),
    customer: parseFilterValue(searchParams.get('customer')),
    site: parseFilterValue(searchParams.get('site')),
    style: parseFilterValue(searchParams.get('style')),
    channel: parseFilterValue(searchParams.get('channel')),
    invoice_no: parseFilterValue(searchParams.get('invoice_no')),
    so_no: parseFilterValue(searchParams.get('so_no')),
    reference_site: parseFilterValue(searchParams.get('reference_site')),
    tat_breach: getTatBreachFromSearchParams(searchParams),
    color: parseFilterValue(searchParams.get('color')),
    created_by: parseFilterValue(searchParams.get('created_by')),
    brand: parseFilterValue(searchParams.get('brand')),
    courier: parseFilterValue(searchParams.get('courier')),
    division: parseFilterValue(searchParams.get('division')),
    seasons: parseFilterValue(searchParams.get('seasons')),
    courier_status: parseFilterValue(searchParams.get('courier_status')),
    unverified_orders: searchParams.get('unverified_orders') || ''
  });

  // Date range state for DateRangePicker
  const [dateRange, setDateRange] = useState([
    filters.start_date ? dayjs(filters.start_date) : null,
    filters.end_date ? dayjs(filters.end_date) : null
  ]);

  // Returns options for a given field, respecting the 100-item cap.
  // When the user has typed a search string, show ALL matching items (no cap).
  const getDisplayOptions = useCallback((field) => {
    const all = allFilterOptionsRef.current[field] || [];
    const inputVal = (filterInputValues[field] || '').trim().toLowerCase();
    if (inputVal) {
      // User is searching — return all matching items
      return all.filter(opt => opt.toLowerCase().includes(inputVal));
    }
    // No search — cap at FILTER_DISPLAY_LIMIT
    return all.slice(0, FILTER_DISPLAY_LIMIT);
  }, [filterInputValues]);

  const convertFiltersToApiParams = (filterValues = {}) => {
    const apiParams = {};
    Object.keys(filterValues).forEach((key) => {
      const value = filterValues[key];
      if (key === 'tat_breach' && Array.isArray(value)) {
        // Convert TAT breach array to individual boolean parameters
        value.forEach((breach) => {
          if (breach === 'Dispatch Breach') {
            apiParams.dispatch_breach = 'true';
          } else if (breach === 'Pickup Breach') {
            apiParams.pickup_breach = 'true';
          } else if (breach === 'Delivery Breach') {
            apiParams.delivery_breach = 'true';
          } else if (breach === 'GRC Breach') {
            apiParams.grc_breach = 'true';
          }
        });
      } else if (Array.isArray(value)) {
        apiParams[key] = value.filter((v) => v).join(',');
      } else if (value !== undefined && value !== null && value !== '') {
        apiParams[key] = value;
      }
    });
    return apiParams;
  };

  useEffect(() => {
    // On first load, check if URL params exist
    if (isFirstLoad) {
      const hasUrlParams = searchParams.toString().length > 0;
      
      if (hasUrlParams) {
        // URL params exist (e.g. from Dashboard alerts or back from detail), use them as-is
        const urlFilters = {
          start_date: searchParams.get('start_date') || '',
          end_date: searchParams.get('end_date') || '',
          date_field: searchParams.get('date_field') || 'SO_DATE',
          status: parseFilterValue(searchParams.get('status')),
          customer: parseFilterValue(searchParams.get('customer')),
          site: parseFilterValue(searchParams.get('site')),
          style: parseFilterValue(searchParams.get('style')),
          channel: parseFilterValue(searchParams.get('channel')),
          invoice_no: parseFilterValue(searchParams.get('invoice_no')),
          so_no: parseFilterValue(searchParams.get('so_no')),
          reference_site: parseFilterValue(searchParams.get('reference_site')),
          tat_breach: getTatBreachFromSearchParams(searchParams),
          color: parseFilterValue(searchParams.get('color')),
          created_by: parseFilterValue(searchParams.get('created_by')),
          brand: parseFilterValue(searchParams.get('brand')),
          courier: parseFilterValue(searchParams.get('courier')),
          division: parseFilterValue(searchParams.get('division')),
          seasons: parseFilterValue(searchParams.get('seasons')),
          courier_status: parseFilterValue(searchParams.get('courier_status')),
          unverified_orders: searchParams.get('unverified_orders') || ''
        };
        setFilters(urlFilters);
        setDateRange([
          urlFilters.start_date ? dayjs(urlFilters.start_date) : null,
          urlFilters.end_date ? dayjs(urlFilters.end_date) : null
        ]);
        fetchSalesOrders(urlFilters);
      } else {
        // No URL params, set today's date (true first load)
        const today = getTodayDate();
        const initialFilters = {
          start_date: today,
          end_date: today,
          date_field: 'SO_DATE',
          status: [],
          customer: [],
          site: [],
          style: [],
          channel: [],
          invoice_no: [],
          so_no: [],
          reference_site: [],
          tat_breach: [],
          color: [],
          created_by: [],
          brand: [],
          courier: [],
          division: [],
          seasons: [],
          courier_status: []
        };
        setFilters(initialFilters);
        setDateRange([dayjs(today), dayjs(today)]);
        
        // Update URL params with today's date
        const params = new URLSearchParams();
        params.set('start_date', today);
        params.set('end_date', today);
        setSearchParams(params);
        
        // Fetch data with today's date
        fetchSalesOrders(initialFilters);
      }
      setIsFirstLoad(false);
      return;
    }

    // Update filters from URL params when URL changes (but not on first load)
    const urlFilters = {
      start_date: searchParams.get('start_date') || '',
      end_date: searchParams.get('end_date') || '',
      date_field: searchParams.get('date_field') || 'SO_DATE',
      status: parseFilterValue(searchParams.get('status')),
      customer: parseFilterValue(searchParams.get('customer')),
      site: parseFilterValue(searchParams.get('site')),
      style: parseFilterValue(searchParams.get('style')),
      channel: parseFilterValue(searchParams.get('channel')),
      invoice_no: parseFilterValue(searchParams.get('invoice_no')),
      so_no: parseFilterValue(searchParams.get('so_no')),
      reference_site: parseFilterValue(searchParams.get('reference_site')),
      tat_breach: getTatBreachFromSearchParams(searchParams),
      color: parseFilterValue(searchParams.get('color')),
      created_by: parseFilterValue(searchParams.get('created_by')),
      brand: parseFilterValue(searchParams.get('brand')),
      courier: parseFilterValue(searchParams.get('courier')),
      division: parseFilterValue(searchParams.get('division')),
      seasons: parseFilterValue(searchParams.get('seasons')),
      courier_status: parseFilterValue(searchParams.get('courier_status')),
      unverified_orders: searchParams.get('unverified_orders') || ''
    };
    setFilters(urlFilters);
    // Update date range picker
    setDateRange([
      urlFilters.start_date ? dayjs(urlFilters.start_date) : null,
      urlFilters.end_date ? dayjs(urlFilters.end_date) : null
    ]);
    const apiFilters = convertFiltersToApiParams(urlFilters);
    fetchSalesOrders(apiFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isFirstLoad]);


  const fetchSalesOrders = async (filterParams = filters) => {
    setLoading(true);
    try {
      const apiParams = convertFiltersToApiParams(filterParams);
      const response = await getSalesOrders(apiParams);
      setSalesOrders(response.data);
    } catch (error) {
      console.error('Error fetching sales orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = useCallback((field, value) => {
    // Only update local state, don't update URL yet
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Check if POD exists for a row
  const checkPodExists = async (soNo) => {
    // Check cache first
    if (podExistsMap.has(soNo)) {
      return podExistsMap.get(soNo);
    }
    
    // Check if already loading
    if (loadingPod.get(soNo)) {
      return false;
    }
    
    setLoadingPod(prev => new Map(prev).set(soNo, true));
    try {
      const detailResponse = await getSalesOrderDetail(soNo);
      const podUri = detailResponse.data?.courier_data?.podGcsUri || 
                     detailResponse.data?.so_data?.POD_GCS_URI ||
                     detailResponse.data?.so_data?.pod_gcs_uri;
      // Validate POD URI - must be a valid non-empty string
      const isValidPodUri = podUri && 
                            typeof podUri === 'string' && 
                            podUri.trim() && 
                            podUri.trim().toLowerCase() !== 'none' &&
                            podUri.trim().toLowerCase() !== 'null' &&
                            podUri.trim() !== '';
      const exists = !!isValidPodUri;
      setPodExistsMap(prev => new Map(prev).set(soNo, exists));
      return exists;
    } catch (error) {
      console.error('Error checking POD:', error);
      setPodExistsMap(prev => new Map(prev).set(soNo, false));
      return false;
    } finally {
      setLoadingPod(prev => {
        const newMap = new Map(prev);
        newMap.delete(soNo);
        return newMap;
      });
    }
  };

  const handleClosePODViewer = useCallback(() => {
    setPodViewerOpen(false);
    setPodGcsUri(null);
  }, []);

  const handleViewPOD = async (row) => {
    const soNo = row.SO_NO;
    setLoadingPod(prev => new Map(prev).set(soNo, true));
    try {
      let podUri = row.POD_GCS_URI || row.pod_gcs_uri;
      
      // If POD_GCS_URI is not in row data, fetch detail to get it
      if (!podUri) {
        const detailResponse = await getSalesOrderDetail(soNo);
        podUri = detailResponse.data?.courier_data?.podGcsUri || 
                 detailResponse.data?.so_data?.POD_GCS_URI ||
                 detailResponse.data?.so_data?.pod_gcs_uri;
      }
      
      if (podUri) {
        setPodGcsUri(podUri);
        setPodViewerOpen(true);
      } else {
        alert('POD not available for this order');
      }
    } catch (error) {
      console.error('Error fetching POD:', error);
      alert('Failed to load POD. Please try again.');
    } finally {
      setLoadingPod(prev => {
        const newMap = new Map(prev);
        newMap.delete(soNo);
        return newMap;
      });
    }
  };

  // POD_GCS_URI is now included in the initial backend query, so no automatic checking needed
  // The backend already provides POD_GCS_URI for all rows that have POD available


  // Helper function to render multi-select Autocomplete with search
  const renderMultiSelectFilter = (label, field, placeholder = `Search ${label.toLowerCase()}...`) => {
    const selectedValues = filters[field] || [];
    // visibleOptions: capped at 100 when no search text, full matching set when user is typing
    const visibleOptions = getDisplayOptions(field);
    const isOpen = !!openDropdowns[field];

    // Keep the data ref current so the stable PaperComponent always reads latest values
    if (!chipsPaperDataRef.current[field]) {
      chipsPaperDataRef.current[field] = { selectedValues, handleFilterChange };
    } else {
      chipsPaperDataRef.current[field].selectedValues = selectedValues;
      chipsPaperDataRef.current[field].handleFilterChange = handleFilterChange;
    }
    // Create the stable PaperComponent once per field — never recreated
    if (!chipsPaperComponents.current[field]) {
      chipsPaperComponents.current[field] = makeChipsPaper(chipsPaperDataRef, field);
    }

    return React.createElement(Autocomplete, {
      multiple: true,
      options: ['SELECT_ALL', ...visibleOptions],
      value: selectedValues,
      inputValue: filterInputValues[field] || '',
      onOpen: () => {
        setOpenDropdowns(prev => ({ ...prev, [field]: true }));
        setFilterInputValues(prev => ({ ...prev, [field]: '' }));
      },
      onClose: () => {
        setOpenDropdowns(prev => ({ ...prev, [field]: false }));
        setFilterInputValues(prev => ({ ...prev, [field]: '' }));
      },
      onInputChange: (event, newInputValue, reason) => {
        // Ignore MUI's "reset" after selection — preserve search text so user can keep typing
        if (reason === 'reset') return;
        setFilterInputValues(prev => ({ ...prev, [field]: newInputValue }));
      },
      onChange: (event, newValue) => {
        if (newValue.includes('SELECT_ALL')) {
          const allSelected = visibleOptions.length > 0 && visibleOptions.every(opt => selectedValues.includes(opt));
          if (allSelected) {
            handleFilterChange(field, selectedValues.filter(v => !visibleOptions.includes(v)));
          } else {
            handleFilterChange(field, [...new Set([...selectedValues, ...visibleOptions])]);
          }
          return;
        }
        handleFilterChange(field, newValue.filter(v => v !== 'SELECT_ALL'));
      },
      disableCloseOnSelect: true,
      // All filtering is done by getDisplayOptions — tell MUI not to re-filter
      filterOptions: (opts) => opts,
      size: 'small',
      getOptionLabel: (option) => {
        if (option === 'SELECT_ALL') {
          const allSelected = visibleOptions.length > 0 && visibleOptions.every(opt => selectedValues.includes(opt));
          return allSelected ? 'Deselect All' : 'Select All';
        }
        return String(option || '');
      },
      isOptionEqualToValue: (option, value) => option === value,
      // Stable PaperComponent — same reference every render, reads live data via ref
      PaperComponent: chipsPaperComponents.current[field],
      renderInput: (params) => {
        const displayText = selectedValues.length === 1
          ? selectedValues[0]
          : selectedValues.length > 1
            ? `${selectedValues.length} selected`
            : '';

        return React.createElement(TextField, {
          ...params,
          label: label,
          placeholder: isOpen ? placeholder : (displayText ? '' : placeholder),
          inputProps: {
            ...params.inputProps,
            // Closed: show selection summary. Open: pass through real input so user can type freely.
            value: !isOpen && displayText ? displayText : params.inputProps.value
          },
          sx: {
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              backgroundColor: '#ffffff',
              minHeight: '40px',
              '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)', borderWidth: '1px' },
              '&:hover fieldset': { borderColor: 'rgba(0, 0, 0, 0.87)' },
              '&.Mui-focused fieldset': { borderColor: 'rgba(0, 0, 0, 0.87)', borderWidth: '1px' }
            }
          }
        });
      },
      renderOption: (props, option, { selected }) => {
        const { key, ...otherProps } = props;
        const isSelectAll = option === 'SELECT_ALL';
        const allSelected = visibleOptions.length > 0 && visibleOptions.every(opt => selectedValues.includes(opt));
        const someSelected = visibleOptions.some(opt => selectedValues.includes(opt)) && !allSelected;
        const checked = isSelectAll ? allSelected : selected;
        const indeterminate = isSelectAll ? someSelected : false;

        return React.createElement('li', { key: key, ...otherProps },
          React.createElement(Checkbox, { checked, indeterminate, size: 'small' }),
          React.createElement('span', { style: { fontSize: '0.875rem' } },
            isSelectAll ? (allSelected ? 'Deselect All' : 'Select All') : option
          )
        );
      },
      renderTags: () => null
    });
  };

  const handleDateRangeChange = (newValue) => {
    setDateRange(newValue);
    setFilters(prev => ({
      ...prev,
      start_date: newValue[0] ? newValue[0].format('YYYY-MM-DD') : '',
      end_date: newValue[1] ? newValue[1].format('YYYY-MM-DD') : ''
    }));
  };

  const buildSearchParams = (targetFilters) => {
    const params = new URLSearchParams();
    Object.keys(targetFilters).forEach((key) => {
      const value = targetFilters[key];
      if (Array.isArray(value)) {
        const filtered = value.filter((v) => v).join(',');
        if (filtered) {
          params.set(key, filtered);
        }
      } else if (value) {
        params.set(key, value);
      }
    });
    return params;
  };

  const handleSearch = useCallback(() => {
    const params = buildSearchParams(filters);
    setSearchParams(params);
    // fetchSalesOrders will be called by useEffect([searchParams])
  }, [filters, setSearchParams]);

  const handleClearFilters = useCallback(() => {
    const emptyFilters = {
      start_date: '',
      end_date: '',
      date_field: 'SO_DATE',
      status: [],
      customer: [],
      site: [],
      style: [],
      channel: [],
      invoice_no: [],
      so_no: [],
      reference_site: [],
      tat_breach: [],
      color: [],
      created_by: [],
      brand: [],
      courier: [],
      division: [],
      seasons: [],
      courier_status: [],
      unverified_orders: ''
    };
    setFilters(emptyFilters);
    setDateRange([null, null]);
    setFilterInputValues({});
    setOpenDropdowns({});
    // Updating searchParams will trigger useEffect([searchParams]) which calls fetchSalesOrders —
    // no need to call fetchSalesOrders here directly (avoids double fetch).
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  const handleDownloadBarcodeWise = async () => {
    setDownloadMenuAnchor(null);
    setDownloading(true);

    try {
      const apiParams = convertFiltersToApiParams(filters);
      const response = await downloadSalesOrderItemsCsv(apiParams);

      const blob = new Blob([response.data], { type: 'text/csv' });
      let filename = `sales_orders_barcode_${new Date().toISOString().split('T')[0]}.csv`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      saveAs(blob, filename);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert(error?.message || 'Failed to export. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadSummaryWise = () => {
    setDownloadMenuAnchor(null);
    if (!filteredSalesOrders.length) return;

    const headers = [
      'SO Date', 'SO Channel', 'SO No', 'SO Status', 'Reference Site', 'PO Number', 'Hold Status', 'Customer Name', 'Created By', 'Authorized By',
      'SO Qty', 'Site', 'Dispatch Date', 'Invoice No', 'Dispatch Qty', 'Shipping Code', 'AWB', 'Courier',
      'Pickup Date', 'Courier Status', 'Delivered Date', 'GRC Date', 'GRC Qty', 'POD'
    ];

    const escapeCsv = (v) => {
      const s = v == null ? '' : String(v);
      if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    const rows = filteredSalesOrders.map((row) => {
      const pickupDisplay = row.pickupDate
        ? formatPickupDate(row.pickupDate, row.pickupTime, row.COURIER || row.COURIER_NAME)
        : '-';
      let deliveredDisplay = '-';
      if (row.courierData) {
        deliveredDisplay = formatDeliveredDate(row.courierData, row.COURIER || row.COURIER_NAME);
      } else if (row.deliveredDate) {
        deliveredDisplay = formatDate(row.deliveredDate);
      }
      const awb = row.UC_AWB || row.DISPATCH_AWB || '-';
      const courierStatus = getCourierStatus(row);
      const podUriInRow = row.POD_GCS_URI || row.pod_gcs_uri;
      const hasPOD = podUriInRow &&
        typeof podUriInRow === 'string' &&
        podUriInRow.trim() &&
        podUriInRow.trim().toLowerCase() !== 'none' &&
        podUriInRow.trim().toLowerCase() !== 'null';

      return [
        row.SO_DATE ? formatDate(row.SO_DATE) : '-',
        formatValue(row.SO_CHANNEL),
        formatValue(row.SO_NO),
        formatValue(row.SO_STATUS) || '-',
        formatValue(row.REFERENCE_SITE),
        formatValue(row.UC_PO_number),
        formatValue(row.UC_On_Hold),
        formatValue(row.CUSTOMER_NAME),
        formatValue(row.CREATED_BY),
        formatValue(row.AUTHORIZED_BY),
        row.SO_QTY ?? '-',
        formatValue(row.OWNER_SITE),
        row.DISPATCH_DATE ? formatDate(row.DISPATCH_DATE) : '-',
        (row.INVOICE_NO && String(row.INVOICE_NO).trim() !== '' && String(row.INVOICE_NO).trim() !== '-') ? String(row.INVOICE_NO).trim() : '-',
        row.DISPATCH_QTY != null && row.DISPATCH_QTY !== '' ? row.DISPATCH_QTY : '-',
        formatValue(row.UC_Shipping_Package_Code),
        formatValue(awb),
        (row.COURIER && row.COURIER !== '-' && row.COURIER !== 'null') ? row.COURIER : '-',
        pickupDisplay,
        courierStatus,
        deliveredDisplay,
        row.GRC_DATE ? formatDate(row.GRC_DATE) : '-',
        row.GRC_QTY != null && row.GRC_QTY !== '' ? row.GRC_QTY : '-',
        hasPOD ? 'Yes' : 'No'
      ].map(escapeCsv);
    });

    const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const filename = `sales_orders_summary_${new Date().toISOString().split('T')[0]}.csv`;
    saveAs(blob, filename);
  };

  const handleRowClick = useCallback((params) => {
    // Preserve current filters in URL when navigating to detail
    // URL encode the SO_NO to handle special characters like slashes
    const encodedSoNo = encodeURIComponent(params.row.SO_NO);
    navigate(`/explore/sora/sales-order/${encodedSoNo}?${searchParams.toString()}`);
  }, [navigate, searchParams]);

  // Helper function to format empty/null values
  const formatValue = useCallback((value) => {
    if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
      return '-';
    }
    return value;
  }, []);

  // Helper function to format BlueDart pickup date: "03 Jan 2026, 02:15 pm" (12-hour format)
  const formatPickupDate = useCallback((pickupDate, pickupTime, courierName) => {
    if (!pickupDate) return '-';
    
    const courier = (courierName || '').toLowerCase();
    const isBlueDart = courier.includes('bluedart') || courier.includes('blue dart');
    
    try {
      const dateStr = String(pickupDate).trim();
      const pickupTimeStr = pickupTime ? String(pickupTime).trim() : '';
      
      // Check if pickupDate already contains time (format: "03 January 2026 1415")
      const timePattern = /\s+(\d{4})\s+(\d{4})$/; // Matches: space + year + space + time
      const match = dateStr.match(timePattern);
      
      let dateObj;
      let combinedStr;
      
      if (match) {
        // Time is already in pickupDate: "03 January 2026 1415"
        const year = match[1];
        const timePart = match[2];
        const datePart = dateStr.replace(/\s+\d{4}\s+\d{4}$/, '').trim();
        const timeFormatted = `${timePart.substring(0, 2)}:${timePart.substring(2)}`;
        combinedStr = `${datePart} ${year} ${timeFormatted}`;
      } else if (pickupTimeStr && pickupTimeStr.length === 4 && /^\d{4}$/.test(pickupTimeStr)) {
        // Separate pickupDate and pickupTime fields (for BlueDart or any courier with 4-digit time)
        const timeFormatted = `${pickupTimeStr.substring(0, 2)}:${pickupTimeStr.substring(2)}`;
        combinedStr = `${dateStr.trim()} ${timeFormatted}`;
      } else {
        // No time available, use default formatDate
        return formatDate(pickupDate);
      }
      
      // Parse "03 January 2026 14:15" format using regex
      const months = {
        'january': 0, 'february': 1, 'march': 2, 'april': 3, 'may': 4, 'june': 5,
        'july': 6, 'august': 7, 'september': 8, 'october': 9, 'november': 10, 'december': 11
      };
      
      const dateTimePattern = /^(\d{1,2})\s+(\w+)\s+(\d{4})\s+(\d{1,2}):(\d{2})$/;
      const dateTimeMatch = combinedStr.match(dateTimePattern);
      
      if (dateTimeMatch) {
        const day = parseInt(dateTimeMatch[1], 10);
        const monthName = dateTimeMatch[2].toLowerCase();
        const year = parseInt(dateTimeMatch[3], 10);
        const hours = parseInt(dateTimeMatch[4], 10);
        const minutes = parseInt(dateTimeMatch[5], 10);
        
        if (months[monthName] !== undefined && !isNaN(day) && !isNaN(year) && !isNaN(hours) && !isNaN(minutes)) {
          dateObj = new Date(year, months[monthName], day, hours, minutes);
        }
      }
      
      if (dateObj && !isNaN(dateObj.getTime())) {
        const day = dateObj.getDate().toString().padStart(2, '0');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[dateObj.getMonth()];
        const year = dateObj.getFullYear();
        const hours24 = dateObj.getHours();
        const minutes = dateObj.getMinutes().toString().padStart(2, '0');
        
        // Format time (12-hour format with am/pm)
        const period = hours24 >= 12 ? 'pm' : 'am';
        const displayHours = hours24 % 12 || 12;
        const formattedTime = `${displayHours.toString().padStart(2, '0')}:${minutes} ${period}`;
        
        return `${day} ${month} ${year}, ${formattedTime}`;
      }
      
      // Fallback to default formatDate
      return formatDate(pickupDate);
    } catch (e) {
      return formatDate(pickupDate);
    }
  }, []);

  // Helper function to extract raw date value for sorting (returns Date object or null)
  const getPickupDateValue = useCallback((row) => {
    if (!row || !row.pickupDate) return null;
    
    try {
      const pickupDateStr = String(row.pickupDate).trim();
      const pickupTimeStr = row.pickupTime ? String(row.pickupTime).trim() : '';
      
      // Check if pickupDate already contains time (Shiprocket format: "03 January 2026 17:31")
      const shiprocketPattern = /(\d{1,2})\s+(\w+)\s+(\d{4})\s+(\d{1,2}):(\d{2})$/;
      const shiprocketMatch = pickupDateStr.match(shiprocketPattern);
      
      if (shiprocketMatch) {
        const day = parseInt(shiprocketMatch[1], 10);
        const monthName = shiprocketMatch[2].toLowerCase();
        const year = parseInt(shiprocketMatch[3], 10);
        const hours = parseInt(shiprocketMatch[4], 10);
        const minutes = parseInt(shiprocketMatch[5], 10);
        
        const monthMap = {
          'january': 0, 'february': 1, 'march': 2, 'april': 3, 'may': 4, 'june': 5,
          'july': 6, 'august': 7, 'september': 8, 'october': 9, 'november': 10, 'december': 11
        };
        
        const monthIndex = monthMap[monthName];
        if (monthIndex !== undefined) {
          return new Date(year, monthIndex, day, hours, minutes);
        }
      }
      
      // Check BlueDart format: "03 January 2026 1415"
      const bluedartPattern = /(\d{1,2})\s+(\w+)\s+(\d{4})\s+(\d{4})$/;
      const bluedartMatch = pickupDateStr.match(bluedartPattern);
      
      if (bluedartMatch) {
        const day = parseInt(bluedartMatch[1], 10);
        const monthName = bluedartMatch[2].toLowerCase();
        const year = parseInt(bluedartMatch[3], 10);
        const timeStr = bluedartMatch[4];
        const hours = parseInt(timeStr.substring(0, 2), 10);
        const minutes = parseInt(timeStr.substring(2), 10);
        
        const monthMap = {
          'january': 0, 'february': 1, 'march': 2, 'april': 3, 'may': 4, 'june': 5,
          'july': 6, 'august': 7, 'september': 8, 'october': 9, 'november': 10, 'december': 11
        };
        
        const monthIndex = monthMap[monthName];
        if (monthIndex !== undefined) {
          return new Date(year, monthIndex, day, hours, minutes);
        }
      }
      
      // Try parsing as ISO date string
      const isoDate = new Date(pickupDateStr);
      if (!isNaN(isoDate.getTime())) {
        return isoDate;
      }
      
      // Try parsing "DD Month YYYY" format
      const datePattern = /(\d{1,2})\s+(\w+)\s+(\d{4})/;
      const dateMatch = pickupDateStr.match(datePattern);
      if (dateMatch) {
        const day = parseInt(dateMatch[1], 10);
        const monthName = dateMatch[2].toLowerCase();
        const year = parseInt(dateMatch[3], 10);
        
        const monthMap = {
          'january': 0, 'february': 1, 'march': 2, 'april': 3, 'may': 4, 'june': 5,
          'july': 6, 'august': 7, 'september': 8, 'october': 9, 'november': 10, 'december': 11
        };
        
        const monthIndex = monthMap[monthName];
        if (monthIndex !== undefined) {
          return new Date(year, monthIndex, day);
        }
      }
    } catch (e) {
      // If parsing fails, return null to prevent errors
      console.error('Error parsing pickup date:', e);
      return null;
    }
  }, []);

  // Helper function to parse scan date format "10-Nov-2025 16:55"
  const parseScanDate = useCallback((scanDate, scanTime) => {
    try {
      const dateParts = scanDate.split('-');
      if (dateParts.length === 3) {
        const day = parseInt(dateParts[0], 10);
        const monthAbbr = dateParts[1].toLowerCase();
        const year = parseInt(dateParts[2], 10);
        
        const monthMap = {
          'jan': 0, 'feb': 1, 'mar': 2, 'apr': 3, 'may': 4, 'jun': 5,
          'jul': 6, 'aug': 7, 'sep': 8, 'oct': 9, 'nov': 10, 'dec': 11
        };
        
        const monthIndex = monthMap[monthAbbr];
        if (monthIndex !== undefined && scanTime) {
          const timeParts = scanTime.split(':');
          const hours = parseInt(timeParts[0], 10);
          const minutes = parseInt(timeParts[1], 10);
          return new Date(year, monthIndex, day, hours, minutes);
        } else if (monthIndex !== undefined) {
          return new Date(year, monthIndex, day);
        }
      }
    } catch (e) {
      // If parsing fails, return null
      return null;
    }
  }, []);

  // Helper function to extract raw delivered date value for sorting (returns Date object or null)
  const getDeliveredDateValue = useCallback((row) => {
    if (!row) return null;
    
    try {
      // Try to get from courierData scans first
      if (row.courierData && row.courierData.scans) {
        const scans = Array.isArray(row.courierData.scans) 
          ? row.courierData.scans 
          : (typeof row.courierData.scans === 'string' ? JSON.parse(row.courierData.scans) : []);
        
        const deliveryPartnerName = row.courierData.delivery_partner_name || row.courierData.deliveryPartnerName;
        const deliveryPartnerLower = (deliveryPartnerName || '').toLowerCase();
        const isMovin = deliveryPartnerLower.includes('movin');
        const isDelhiveryEnterprise = deliveryPartnerLower.includes('delhivery') && deliveryPartnerLower.includes('enterprise');
        
        for (const scan of scans) {
          if (scan) {
            const scanType = String(scan.scanType || '').toUpperCase();
            const scanStatus = String(scan.status || '').toUpperCase();
            const remarks = String(scan.remarks || scan.Remarks || '').toLowerCase();
            
            // Movin-specific: Look for scanType = "DELIVERED_TO_CUSTOMER"
            if (isMovin && scanType === 'DELIVERED_TO_CUSTOMER') {
              const scanDate = scan.scanDate;
              const scanTime = scan.scanTime;
              if (scanDate && scanTime) {
                return parseScanDate(scanDate, scanTime);
              }
            }
            
            // Delhivery Enterprise-specific: Look for remarks containing "Shipment Delivered"
            if (isDelhiveryEnterprise && remarks && (
              remarks.includes('shipment delivered') || 
              remarks.includes('delivered') ||
              (scanType && scanType.includes('DELIVERED'))
            )) {
              const scanDate = scan.scanDate;
              const scanTime = scan.scanTime;
              if (scanDate && scanTime) {
                return parseScanDate(scanDate, scanTime);
              }
            }
            
            // Generic delivered scan detection
            if (!isMovin && !isDelhiveryEnterprise) {
              const isDelivered = (
                scanType === 'DL' ||
                scanType.includes('DELIVERED') ||
                scanStatus.includes('DELIVERED')
              );
              
              if (isDelivered) {
                const scanDate = scan.scanDate;
                const scanTime = scan.scanTime;
                if (scanDate && scanTime) {
                  return parseScanDate(scanDate, scanTime);
                }
              }
            }
          }
        }
      }
      
      // Fallback to deliveredDate field
      if (row.deliveredDate) {
        const deliveredDate = new Date(row.deliveredDate);
        if (!isNaN(deliveredDate.getTime())) {
          return deliveredDate;
        }
      }
      
      return null;
    } catch (error) {
      // If any error occurs, return null to prevent crashing
      console.error('Error extracting delivered date value:', error);
      return null;
    }
  }, [parseScanDate]);

  // Helper function to format BlueDart delivered date: "10 Jan 2025, 04:55 pm" (12-hour format)
  const formatDeliveredDate = useCallback((courierData, courierName) => {
    if (!courierData) return '-';
    
    const courier = (courierName || '').toLowerCase();
    const isBlueDart = courier.includes('bluedart') || courier.includes('blue dart');
    const isEkart = courier.includes('ekart');
    
    try {
      // For Ekart, check eventList for delivered event (event="D")
      if (isEkart) {
        const eventList = courierData.eventList || courierData.scans || [];
        let eventsArray = eventList;
        
        // Handle if eventList is a string (JSON)
        if (typeof eventList === 'string') {
          try {
            eventsArray = JSON.parse(eventList);
          } catch {
            eventsArray = [];
          }
        }
        
        // Find event with event="D" (Delivered)
        for (const event of eventsArray) {
          if (event && (event.event === 'D' || event.Event === 'D')) {
            const eventDateTime = event.eventDateTime || event.event_date_time || event.EventDateTime;
            if (eventDateTime) {
              // Parse "2026-01-06 18:29:29" format
              try {
                const dateObj = new Date(eventDateTime);
                if (!isNaN(dateObj.getTime())) {
                  const day = dateObj.getDate().toString().padStart(2, '0');
                  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                  const month = monthNames[dateObj.getMonth()];
                  const year = dateObj.getFullYear();
                  const hours24 = dateObj.getHours();
                  const minutes = dateObj.getMinutes().toString().padStart(2, '0');
                  
                  // Format time (12-hour format with am/pm)
                  const period = hours24 >= 12 ? 'pm' : 'am';
                  const displayHours = hours24 % 12 || 12;
                  const formattedTime = `${displayHours.toString().padStart(2, '0')}:${minutes} ${period}`;
                  
                  return `${day} ${month} ${year}, ${formattedTime}`;
                }
              } catch {
                // Fall through to use formatDate
              }
            }
            break;
          }
        }
      }
      
      // For BlueDart, try to get delivered date from scans array (scanType == "DL")
      let deliveryDate = null;
      let deliveryTime = null;
      
      const scans = courierData.scans || [];
      let scansArray = scans;
      
      // Handle if scans is a string (JSON)
      if (typeof scans === 'string') {
        try {
          scansArray = JSON.parse(scans);
        } catch {
          scansArray = [];
        }
      }
      
      // Partner-specific logic: Check for Movin and Delhivery Enterprise delivery partners
      const deliveryPartnerName = courierData.delivery_partner_name || courierData.deliveryPartnerName;
      const deliveryPartnerLower = (deliveryPartnerName || '').toLowerCase();
      const isMovin = deliveryPartnerLower.includes('movin');
      const isDelhiveryEnterprise = deliveryPartnerLower.includes('delhivery') && deliveryPartnerLower.includes('enterprise');
      
      // Find scan with delivered status
      for (const scan of scansArray) {
        if (scan) {
          const scanType = String(scan.scanType || '').toUpperCase();
          const scanStatus = String(scan.status || '').toUpperCase();
          const remarks = String(scan.remarks || scan.Remarks || '').toLowerCase();
          
          // Movin-specific: Look for scanType = "DELIVERED_TO_CUSTOMER"
          if (isMovin && scanType === 'DELIVERED_TO_CUSTOMER') {
            deliveryDate = scan.scanDate; // Format: "10-Nov-2025"
            deliveryTime = scan.scanTime; // Format: "16:55"
            break;
          }
          
          // Delhivery Enterprise-specific: Look for remarks containing "Shipment Delivered"
          if (isDelhiveryEnterprise && remarks && (
            remarks.includes('shipment delivered') || 
            remarks.includes('delivered') ||
            (scanType && scanType.includes('DELIVERED'))
          )) {
            deliveryDate = scan.scanDate; // Format: "10-Nov-2025"
            deliveryTime = scan.scanTime; // Format: "16:55"
            break;
          }
          
          // Generic delivered scan detection (for other partners)
          if (!isMovin && !isDelhiveryEnterprise) {
            const isDelivered = (
              scanType === 'DL' ||  // Support legacy generic value
              scanType.includes('DELIVERED') ||
              scanStatus.includes('DELIVERED')
            );
            
            if (isDelivered) {
              deliveryDate = scan.scanDate; // Format: "10-Nov-2025"
              deliveryTime = scan.scanTime; // Format: "16:55"
              break;
            }
          }
        }
      }
      
      // Fallback to deliveredTime if not found in scans
      if (!deliveryDate) {
        const deliveredTimeField = courierData.deliveredDate || courierData.delivered_date || courierData.deliveryDate || courierData.deliveredTime;
        if (deliveredTimeField) {
          return formatDate(deliveredTimeField);
        }
        return '-';
      }
      
      // For BlueDart, format as "10 Jan 2025, 04:55 pm"
      if (isBlueDart && deliveryDate && deliveryTime) {
        // Parse "10-Nov-2025" format
        const dateParts = deliveryDate.split('-');
        if (dateParts.length === 3) {
          const day = parseInt(dateParts[0], 10);
          const monthAbbr = dateParts[1].toLowerCase();
          const year = parseInt(dateParts[2], 10);
          
          const monthMap = {
            'jan': 0, 'feb': 1, 'mar': 2, 'apr': 3, 'may': 4, 'jun': 5,
            'jul': 6, 'aug': 7, 'sep': 8, 'oct': 9, 'nov': 10, 'dec': 11
          };
          
          const monthIndex = monthMap[monthAbbr];
          
          // Parse time "16:55"
          const timeParts = deliveryTime.split(':');
          const hours = parseInt(timeParts[0], 10);
          const minutes = parseInt(timeParts[1], 10);
          
          if (monthIndex !== undefined && !isNaN(day) && !isNaN(year) && !isNaN(hours) && !isNaN(minutes)) {
            const dateObj = new Date(year, monthIndex, day, hours, minutes);
            if (!isNaN(dateObj.getTime())) {
              const formattedDay = dateObj.getDate().toString().padStart(2, '0');
              const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              const month = monthNames[dateObj.getMonth()];
              const formattedYear = dateObj.getFullYear();
              const hours24 = dateObj.getHours();
              const formattedMinutes = dateObj.getMinutes().toString().padStart(2, '0');
              
              // Format time (12-hour format with am/pm)
              const period = hours24 >= 12 ? 'pm' : 'am';
              const displayHours = hours24 % 12 || 12;
              const formattedTime = `${displayHours.toString().padStart(2, '0')}:${formattedMinutes} ${period}`;
              
              return `${formattedDay} ${month} ${formattedYear}, ${formattedTime}`;
            }
          }
        }
      }
      
      // Fallback: combine date and time and use formatDate
      if (deliveryDate && deliveryTime) {
        const combined = `${deliveryDate} ${deliveryTime}`;
        return formatDate(combined);
      }
      
      return formatDate(deliveryDate || courierData.deliveredTime);
    } catch (e) {
      const deliveredTimeField = courierData.deliveredDate || courierData.delivered_date || courierData.deliveryDate || courierData.deliveredTime;
      return formatDate(deliveredTimeField) || '-';
    }
  }, []);

  // Helper function to get courier status for a row
  const getCourierStatus = useCallback((row) => {
    // Use COURIER_STATUS from backend (only set if courier-specific status available from BigQuery)
    // Return '-' if not available (no generic fallbacks)
    return row.COURIER_STATUS || '-';
  }, []);

  // Filter sales orders - backend handles courier_status filtering now
  // Only apply client-side filters for other fields if needed
  const filteredSalesOrders = useMemo(() => {
    // Backend now handles courier_status filtering, so we just return salesOrders
    // (which are already filtered by the backend)
    return salesOrders;
  }, [salesOrders]);

  const columns = useMemo(() => [
    {
      field: 'SO_DATE',
      headerName: 'SO Date',
      width: 180,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => (value ? formatDate(value) : '-')
    },
    {
      field: 'SO_CHANNEL',
      headerName: 'SO Channel',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => formatValue(value)
    },
    {
      field: 'SO_NO',
      headerName: 'SO No',
      width: 140,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        return React.createElement(Box, {
          sx: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%'
          }
        },
          React.createElement(Typography, {
            variant: 'body2',
            sx: {
              color: 'text.primary',
              cursor: 'pointer',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              '&:hover': {
                textDecoration: 'underline'
              }
            }
          }, formatValue(params.value))
        );
      }
    },
    {
      field: 'SO_STATUS',
      headerName: 'SO Status',
      width: 110,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const status = formatValue(params.value) || '-';
        return React.createElement(Box, {
          sx: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%'
          }
        },
          React.createElement(Chip, {
            label: status,
            size: 'small',
            sx: {
              backgroundColor: status === 'COMPLETE' ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
              border: '1px solid',
              borderColor: 'divider',
              color: 'text.primary',
              fontWeight: 500,
              borderRadius: 2
            }
          })
        );
      }
    },
    {
      field: 'REFERENCE_SITE',
      headerName: 'Reference Site',
      width: 180,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        return React.createElement(Box, {
          sx: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            overflow: 'hidden'
          }
        },
          React.createElement(Typography, {
            variant: 'body2',
            title: formatValue(params.value),
            sx: {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%'
            }
          }, formatValue(params.value))
        );
      }
    },
    {
      field: 'UC_PO_number',
      headerName: 'PO Number',
      width: 130,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => formatValue(value)
    },
    {
      field: 'UC_On_Hold',
      headerName: 'Hold Status',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => formatValue(value)
    },
    {
      field: 'CUSTOMER_NAME',
      headerName: 'Customer Name',
      width: 250,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        return React.createElement(Box, {
          sx: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            overflow: 'hidden'
          }
        },
          React.createElement(Typography, {
            variant: 'body2',
            title: formatValue(params.value),
            sx: {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%'
            }
          }, formatValue(params.value))
        );
      }
    },
    {
      field: 'CREATED_BY',
      headerName: 'Created By',
      width: 160,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => formatValue(value)
    },
    {
      field: 'AUTHORIZED_BY',
      headerName: 'Authorized By',
      width: 160,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => formatValue(value)
    },
    {
      field: 'SO_QTY',
      headerName: 'SO Qty',
      width: 80,
      type: 'number',
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => formatValue(value)
    },
    {
      field: 'OWNER_SITE',
      headerName: 'Site',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => formatValue(value)
    },
    {
      field: 'DISPATCH_DATE',
      headerName: 'Dispatch Date',
      width: 180,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => (value ? formatDate(value) : '-')
    },
    {
      field: 'INVOICE_NO',
      headerName: 'Invoice No',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => {
        // Treat "-" and blank as empty so we never show literal "-" as the invoice number
        if (value == null || value === '' || value === '-' || String(value).trim() === '') {
          return '-';
        }
        return String(value).trim();
      }
    },
    {
      field: 'DISPATCH_QTY',
      headerName: 'Dispatch Qty',
      width: 120,
      type: 'number',
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => {
        if (value === null || value === undefined || value === '' || value === 0) {
          return '-';
        }
        return value;
      }
    },
    {
      field: 'UC_Shipping_Package_Code',
      headerName: 'Shipping Code',
      width: 130,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => formatValue(value)
    },
    {
      field: 'AWB',
      headerName: 'AWB',
      width: 180,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        // Try UC_AWB first, then DISPATCH_AWB
        const awb = params?.row?.UC_AWB || params?.row?.DISPATCH_AWB || '-';
        return React.createElement(Box, {
          sx: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%'
          }
        },
          React.createElement(Typography, {
            variant: 'body2',
            sx: { whiteSpace: 'nowrap' }
          }, formatValue(awb))
        );
      }
    },
    {
      field: 'COURIER',
      headerName: 'Courier',
      width: 140,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => {
        if (value === null || value === undefined || value === '' || value === '-' || value === 'null' || value === 'undefined') {
          return '-';
        }
        return value;
      }
    },
    {
      field: 'PICKUP_DATE',
      headerName: 'Pickup Date',
      width: 180,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (value, row) => {
        // Return Date object for proper sorting
        try {
          return getPickupDateValue(row);
        } catch (error) {
          console.error('Error in Pickup Date valueGetter:', error);
          return null;
        }
      },
      sortComparator: (v1, v2) => {
        // Custom sort comparator for dates
        if (!v1 && !v2) return 0;
        if (!v1) return 1; // null values go to end
        if (!v2) return -1; // null values go to end
        return v1.getTime() - v2.getTime();
      },
      renderCell: (params) => {
        // Pickup date from courier tracking API - format BlueDart as "03 Jan 2026, 02:15 pm" (12-hour format)
        const row = params.row;
        const displayValue = row.pickupDate 
          ? formatPickupDate(row.pickupDate, row.pickupTime, row.COURIER || row.COURIER_NAME) 
          : '-';
        return React.createElement(Box, {
          sx: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%'
          }
        },
          React.createElement(Typography, { variant: 'body2', sx: { whiteSpace: 'nowrap' } }, displayValue)
        );
      }
    },
    {
      field: 'COURIER_STATUS',
      headerName: 'Courier Status',
      width: 140,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        // Use COURIER_STATUS from backend (exact value from BigQuery courier tables)
        // Only shows status if available from BLUEDART status field or EKART docketTrackingStatus
        // Shows '-' if courier status not available (no generic fallbacks)
        const row = params.row;
        const courierStatus = row.COURIER_STATUS || '-';
        
        // Map status to colors
        let statusColor = { bg: 'transparent', border: 'divider' };
        const statusUpper = String(courierStatus).toUpperCase();
        
        if (statusUpper === 'SHIPMENT DELIVERED' || statusUpper === 'DELIVERED') {
          statusColor = { bg: 'rgba(76, 175, 80, 0.1)', border: 'rgba(76, 175, 80, 0.5)' };
        } else if (statusUpper === 'ONLINE SHIPMENT BOOKED') {
          statusColor = { bg: 'rgba(156, 39, 176, 0.1)', border: 'rgba(156, 39, 176, 0.5)' };
        } else if (statusUpper === 'PICKUP EMPLOYEE IS OUT TO P/U SHIPMENT') {
          statusColor = { bg: 'rgba(33, 150, 243, 0.1)', border: 'rgba(33, 150, 243, 0.5)' };
        }
        
        return React.createElement(Box, {
          sx: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%'
          }
        },
          courierStatus === '-' 
            ? React.createElement(Typography, { variant: 'body2', sx: { color: 'text.secondary' } }, '-')
            : React.createElement(Chip, {
                label: courierStatus,
                size: 'small',
                sx: {
                  backgroundColor: statusColor.bg,
                  border: '1px solid',
                  borderColor: statusColor.border,
                  color: 'text.primary',
                  fontWeight: 500,
                  borderRadius: 2
                }
              })
        );
      }
    },
    {
      field: 'DELIVERED_DATE',
      headerName: 'Delivered Date',
      width: 180,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (value, row) => {
        // Return Date object for proper sorting
        try {
          return getDeliveredDateValue(row);
        } catch (error) {
          console.error('Error in Delivered Date valueGetter:', error);
          return null;
        }
      },
      sortComparator: (v1, v2) => {
        // Custom sort comparator for dates
        if (!v1 && !v2) return 0;
        if (!v1) return 1; // null values go to end
        if (!v2) return -1; // null values go to end
        return v1.getTime() - v2.getTime();
      },
      renderCell: (params) => {
        // Delivered date from courier tracking API - format BlueDart as "10 Jan 2025, 04:55 pm" (12-hour format)
        const row = params.row;
        let displayValue = '-';
        
        // If courierData is available (full courier tracking object), use formatDeliveredDate
        if (row.courierData) {
          displayValue = formatDeliveredDate(row.courierData, row.COURIER || row.COURIER_NAME);
        } 
        // Otherwise, if deliveredDate is a simple date string, use formatDate
        else if (row.deliveredDate) {
          displayValue = formatDate(row.deliveredDate);
        }
        
        return React.createElement(Box, {
          sx: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%'
          }
        },
          React.createElement(Typography, { variant: 'body2', sx: { whiteSpace: 'nowrap' } }, displayValue)
        );
      }
    },
    {
      field: 'GRC_DATE',
      headerName: 'GRC Date',
      width: 180,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => (value ? formatDate(value) : '-')
    },
    {
      field: 'GRC_QTY',
      headerName: 'GRC Qty',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => {
        if (value === null || value === undefined || value === '' || value === 0) {
          return '-';
        }
        return value;
      }
    },
    {
      field: 'POD_VIEW',
      headerName: 'POD',
      width: 110,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const row = params.row;
        const soNo = row.SO_NO;
        
        // Only show POD button if shipment is delivered
        const courierStatus = row.COURIER_STATUS || '';
        const statusUpper = String(courierStatus).toUpperCase();
        const isDelivered = statusUpper === 'SHIPMENT DELIVERED' || statusUpper === 'DELIVERED' || row.GRC_DATE;
        
        // Only show enabled button when POD URL is actually available and valid
        const podUriInRow = row.POD_GCS_URI || row.pod_gcs_uri;
        // Validate POD URI - must be a non-empty string and not 'none', 'null', empty string, etc.
        const isValidPodUri = podUriInRow && 
                              typeof podUriInRow === 'string' && 
                              podUriInRow.trim() && 
                              podUriInRow.trim().toLowerCase() !== 'none' &&
                              podUriInRow.trim().toLowerCase() !== 'null' &&
                              podUriInRow.trim() !== '';
        // POD_GCS_URI is now included in the initial backend query, so we only need to check row data
        // Keep podExistsMap check as fallback for edge cases where POD might be fetched manually
        const podExistsInCache = podExistsMap.get(soNo);
        const hasPOD = (isValidPodUri || podExistsInCache === true) && isDelivered;
        const isLoadingThisRow = loadingPod.get(soNo) || false;
        
        // Don't show button at all if no POD available
        if (!hasPOD) {
          return React.createElement(Box, {
            sx: {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '100%'
            }
          },
            React.createElement(Typography, {
              variant: 'body2',
              sx: { color: 'text.secondary' }
            }, '-')
          );
        }
        
        return React.createElement(Box, {
          sx: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%'
          }
        },
          React.createElement(Button, {
            size: 'small',
            variant: 'outlined',
            startIcon: isLoadingThisRow ? React.createElement(CircularProgress, { size: 14 }) : React.createElement(VisibilityIcon, null),
            disabled: isLoadingThisRow,
            onClick: (e) => {
              e.stopPropagation();
              handleViewPOD(row);
            },
            sx: {
              textTransform: 'none',
              borderColor: 'success.main',
              color: 'success.main',
              fontSize: '0.75rem',
              minWidth: 'auto',
              px: 1,
              '&:hover': {
                borderColor: 'success.dark',
                backgroundColor: 'rgba(76, 175, 80, 0.04)',
              },
              '&:disabled': {
                opacity: 0.5
              }
            }
          }, 'View POD')
        );
      }
    }
  ], [formatPickupDate, formatDeliveredDate, formatValue, getCourierStatus, handleViewPOD, podExistsMap, loadingPod, getPickupDateValue, getDeliveredDateValue, parseScanDate]);
  
  // Note: podExistsMap and loadingPod are kept for backward compatibility and edge cases
  // but POD_GCS_URI is now provided by the backend in the initial query

  return React.createElement(Container, {
    maxWidth: 'xl',
    sx: {
      px: { xs: 2, sm: 3, md: 4 },
      pt: { xs: 1, sm: 1.5, md: 2 },
      mt: 0
    }
  },
    React.createElement(Box, {
      sx: {
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        p: { xs: 2, sm: 3 },
        mb: 4,
        mt: 1,
        backgroundColor: 'background.paper',
        position: 'relative'
      }
    },
      filterOptionsLoading && React.createElement(Box, {
        sx: {
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          borderRadius: 3
        }
      },
        React.createElement(CircularProgress, { size: 40 })
      ),
      React.createElement(Typography, {
        variant: 'h6',
        sx: {
          mb: 3,
          fontWeight: 600,
          fontSize: '1.125rem'
        }
      }, 'Filters'),
      React.createElement(Grid, { container: true, spacing: 2 },
        // Row 1: Style, Channel, Invoice No, SO No., Reference Site, Barcode Status
        React.createElement(Grid, { item: true, xs: 12, sm: 6, md: 2 },
          renderMultiSelectFilter('Style', 'style', 'Search style...')
        ),
        React.createElement(Grid, { item: true, xs: 12, sm: 6, md: 2 },
          renderMultiSelectFilter('Channel', 'channel', 'Search channel...')
        ),
        React.createElement(Grid, { item: true, xs: 12, sm: 6, md: 2 },
          renderMultiSelectFilter('Invoice No', 'invoice_no', 'Search invoice no...')
        ),
        React.createElement(Grid, { item: true, xs: 12, sm: 6, md: 2 },
          renderMultiSelectFilter('SO No.', 'so_no', 'Search SO no...')
        ),
        React.createElement(Grid, { item: true, xs: 12, sm: 6, md: 2 },
          renderMultiSelectFilter('Reference Site', 'reference_site', 'Search reference site...')
        ),
        React.createElement(Grid, { item: true, xs: 12, sm: 6, md: 2 },
          renderMultiSelectFilter('Customer Name', 'customer', 'Search customer name...')
        ),
        React.createElement(Grid, { item: true, xs: 12, sm: 6, md: 2 },
          renderMultiSelectFilter('TAT Breach', 'tat_breach', 'Search TAT breach...')
        ),
        // Row 2: Colour, Created By, Brand, SO Status, Courier, Date Range (Start), Date Range (End)
        React.createElement(Grid, { item: true, xs: 12, sm: 6, md: 2 },
          renderMultiSelectFilter('Colour', 'color', 'Search colour...')
        ),
        React.createElement(Grid, { item: true, xs: 12, sm: 6, md: 2 },
          renderMultiSelectFilter('Created By', 'created_by', 'Search created by...')
        ),
        React.createElement(Grid, { item: true, xs: 12, sm: 6, md: 2 },
          renderMultiSelectFilter('Brand', 'brand', 'Search brand...')
        ),
        React.createElement(Grid, { item: true, xs: 12, sm: 6, md: 2 },
          renderMultiSelectFilter('SO Status', 'status', 'Search SO status...')
        ),
        React.createElement(Grid, { item: true, xs: 12, sm: 6, md: 2 },
          renderMultiSelectFilter('Courier', 'courier', 'Search courier...')
        ),
        React.createElement(Grid, { item: true, xs: 12, sm: 6, md: 2 },
          renderMultiSelectFilter('Courier Status', 'courier_status', 'Search courier status...')
        ),
        React.createElement(Grid, { item: true, xs: 12, sm: 6, md: 2 },
          renderMultiSelectFilter('Division', 'division', 'Search division...')
        ),
        React.createElement(Grid, { item: true, xs: 12, sm: 6, md: 2 },
          renderMultiSelectFilter('Seasons', 'seasons', 'Search seasons...')
        ),
        React.createElement(Grid, { item: true, xs: 12, sm: 6, md: 2 },
          React.createElement(Box, {
            sx: {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              height: '100%',
              gap: 1
            }
          },
            React.createElement(Typography, {
              variant: 'body2',
              sx: {
                fontSize: '0.875rem',
                color: (filters.date_field || 'SO_DATE') === 'SO_DATE' ? 'text.primary' : 'text.secondary',
                fontWeight: (filters.date_field || 'SO_DATE') === 'SO_DATE' ? 500 : 400
              }
            }, 'SO Date'),
            React.createElement(Switch, {
              checked: (filters.date_field || 'SO_DATE') === 'DISPATCH_DATE',
              onChange: (e) => {
                handleFilterChange('date_field', e.target.checked ? 'DISPATCH_DATE' : 'SO_DATE');
              },
              size: 'small'
            }),
            React.createElement(Typography, {
              variant: 'body2',
              sx: {
                fontSize: '0.875rem',
                color: (filters.date_field || 'SO_DATE') === 'DISPATCH_DATE' ? 'text.primary' : 'text.secondary',
                fontWeight: (filters.date_field || 'SO_DATE') === 'DISPATCH_DATE' ? 500 : 400
              }
            }, 'Dispatch Date')
          )
        ),
        React.createElement(Grid, { item: true, xs: 12, sm: 6, md: 4 },
          React.createElement(LocalizationProvider, { dateAdapter: AdapterDayjs },
            React.createElement(Box, {
              sx: {
                display: 'flex',
                gap: 1,
                alignItems: 'center'
              }
            },
              React.createElement(DatePicker, {
                label: 'Start Date',
                value: dateRange[0],
                onChange: (newValue) => {
                  const updatedRange = [newValue, dateRange[1]];
                  setDateRange(updatedRange);
                  handleDateRangeChange(updatedRange);
                },
                format: 'DD-MM-YYYY',
                slotProps: {
                  textField: {
                    size: 'small',
                    sx: {
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: '#ffffff',
                        minHeight: '40px',
                        '& fieldset': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                          borderWidth: '1px'
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(0, 0, 0, 0.87)'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'rgba(0, 0, 0, 0.87)',
                          borderWidth: '1px'
                        }
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.875rem',
                        color: 'rgba(0, 0, 0, 0.6)',
                        '&.Mui-focused': {
                          color: 'rgba(0, 0, 0, 0.87)'
                        }
                      }
                    }
                  }
                }
              }),
              React.createElement(Typography, {
                sx: { color: 'text.secondary', px: 1 }
              }, 'to'),
              React.createElement(DatePicker, {
                label: 'End Date',
                value: dateRange[1],
                onChange: (newValue) => {
                  const updatedRange = [dateRange[0], newValue];
                  setDateRange(updatedRange);
                  handleDateRangeChange(updatedRange);
                },
                format: 'DD-MM-YYYY',
                slotProps: {
                  textField: {
                    size: 'small',
                    sx: {
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: '#ffffff',
                        minHeight: '40px',
                        '& fieldset': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                          borderWidth: '1px'
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(0, 0, 0, 0.87)'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'rgba(0, 0, 0, 0.87)',
                          borderWidth: '1px'
                        }
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.875rem',
                        color: 'rgba(0, 0, 0, 0.6)',
                        '&.Mui-focused': {
                          color: 'rgba(0, 0, 0, 0.87)'
                        }
                      }
                    }
                  }
                }
              })
            )
          )
        )
      ),
      React.createElement(Box, { sx: { mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap', alignItems: 'center' } },
        React.createElement(Button, {
          variant: 'outlined',
          startIcon: downloading ? null : React.createElement(DownloadIcon, null),
          onClick: (e) => setDownloadMenuAnchor(e.currentTarget),
          disabled: filteredSalesOrders.length === 0 || loading || downloading,
          sx: {
            borderColor: 'divider',
            color: 'text.primary',
            textTransform: 'none',
            borderRadius: 2,
            fontWeight: 500,
            height: '40px',
            minWidth: 140,
            '&:hover': {
              borderColor: 'text.primary',
              backgroundColor: 'rgba(0, 0, 0, 0.02)',
            },
            '&:disabled': {
              opacity: 0.5
            }
          }
        }, downloading ? 'Preparing...' : 'Download'),
        React.createElement(Menu, {
          anchorEl: downloadMenuAnchor,
          open: Boolean(downloadMenuAnchor),
          onClose: () => setDownloadMenuAnchor(null),
          anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
          transformOrigin: { vertical: 'top', horizontal: 'right' }
        },
          React.createElement(MenuItem, {
            onClick: handleDownloadBarcodeWise,
            disabled: downloading
          }, 'Barcode wise'),
          React.createElement(MenuItem, {
            onClick: handleDownloadSummaryWise,
            disabled: downloading
          }, 'Summary wise')
        ),
        downloading && React.createElement(CircularProgress, {
          size: 20,
          sx: { color: 'text.primary' }
        }),
        React.createElement(Button, {
          variant: 'outlined',
          onClick: handleClearFilters,
          sx: {
            minWidth: 120,
            height: '40px',
            borderColor: 'divider',
            color: 'text.primary',
            textTransform: 'none',
            borderRadius: 2,
            fontWeight: 500,
            '&:hover': {
              borderColor: 'text.primary',
              backgroundColor: 'rgba(0, 0, 0, 0.02)',
            }
          }
        }, 'Clear Filters'),
        React.createElement(Button, {
          variant: 'contained',
          onClick: handleSearch,
          sx: {
            minWidth: 120,
            height: '40px',
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            textTransform: 'none',
            borderRadius: 2,
            fontWeight: 500,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
            }
          }
        }, 'Search')
      )
    ),
    React.createElement(Box, {
      sx: {
        height: 600,
        width: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        overflow: 'hidden'
      }
    },
      React.createElement(DataGrid, {
        rows: filteredSalesOrders,
        columns: columns,
        loading: loading,
        getRowId: (row) => row.SO_NO,
        pageSizeOptions: [25, 50, 100],
        initialState: {
          pagination: {
            paginationModel: { pageSize: 50 }
          }
        },
        columnBuffer: 2,
        columnThreshold: 2,
        disableColumnReorder: false,
        disableColumnResize: false,
        onRowClick: handleRowClick,
        sx: {
          border: 'none',
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.02)',
            }
          },
          '& .MuiDataGrid-cell': {
            borderColor: 'divider',
            whiteSpace: 'nowrap',
            overflow: 'visible',
          },
          '& .MuiDataGrid-cellContent': {
            overflow: 'visible',
            textOverflow: 'clip',
          },
          '& .MuiDataGrid-columnHeaders': {
            borderColor: 'divider',
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            fontWeight: 600,
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            whiteSpace: 'nowrap',
            overflow: 'visible',
            textOverflow: 'clip',
          },
          '& .MuiDataGrid-footerContainer': {
            borderColor: 'divider',
          },
          '& .MuiDataGrid-virtualScroller': {
            overflowX: 'auto',
          }
        }
      }),
      React.createElement(PODViewer, {
        open: podViewerOpen,
        onClose: handleClosePODViewer,
        podGcsUri: podGcsUri
      })
    )
  );
};

export default SalesOrderList;

